import { z } from 'zod';
export const cdlitAiBookingRoutingSchema = z
    .object({
    /** Один салон: все услуги → этот company id (если нет строки в serviceToCompany). */
    defaultCompanyId: z.string().min(1).optional(),
    serviceToCompany: z.record(z.string().min(1), z.string().min(1)),
    staffTitles: z.record(z.string().min(1), z.string().min(1)).optional(),
})
    .strict();
/** Публичная видимость ассистента на landing-cdlit (не Telegram). */
export const cdlitSiteAssistantVisibilitySchema = z.enum(['off', 'admin_only', 'public']);
export const cdlitAiDecisionLayerModeSchema = z.enum(['off', 'shadow', 'active']);
export const cdlitAiDecisionLayerSchema = z
    .object({
    mode: cdlitAiDecisionLayerModeSchema,
    provider: z.literal('openrouter'),
    model: z.string().trim().min(1).max(200),
    connectionKey: z.literal('default'),
    minConfidence: z.number().min(0).max(1),
    timeoutMs: z.number().int().min(100).max(10000),
    policyVersion: z.string().trim().min(1).max(120),
})
    .strict();
export function defaultCdlitAiDecisionLayer() {
    return {
        mode: 'off',
        provider: 'openrouter',
        model: 'typesafe/jev-1.13',
        connectionKey: 'default',
        minConfidence: 0.9,
        timeoutMs: 1200,
        policyVersion: 'routing-v1',
    };
}
export const cdlitAiCapabilityConnectionSourceSchema = z.enum(['direct_api', 'ai_platform']);
export const cdlitAiCapabilityConnectionRefSchema = z
    .object({
    source: cdlitAiCapabilityConnectionSourceSchema,
    providerId: z.string().trim().min(1).max(120),
    connectionKey: z.string().trim().min(1).max(120).optional(),
    modelId: z.string().trim().min(1).max(200).optional(),
})
    .strict();
export const cdlitAiCapabilityProfileBindingSchema = z
    .object({
    primary: cdlitAiCapabilityConnectionRefSchema,
    fallback: cdlitAiCapabilityConnectionRefSchema.optional(),
})
    .strict();
export const cdlitAiCapabilityIdSchema = z.enum([
    'decision.route',
    'reasoning.simple',
    'reasoning.main',
    'reasoning.complex',
    'embeddings.documents',
    'rerank.documents',
    'vision.general',
    'document.extract',
    'speech.transcribe',
    'speech.diarize',
]);
export const cdlitAiCapabilityBindingsSchema = z.record(cdlitAiCapabilityIdSchema, cdlitAiCapabilityProfileBindingSchema);
export const cdlitAiProductSettingsSchema = z
    .object({
    /** Legacy; синхронизируется с `siteAssistantVisibility` при сохранении из админки. */
    aiEnabled: z.boolean(),
    availabilityEnabled: z.boolean(),
    bookingEnabled: z.boolean(),
    bookingRouting: cdlitAiBookingRoutingSchema,
    siteAssistantVisibility: cdlitSiteAssistantVisibilitySchema.optional(),
    decisionLayer: cdlitAiDecisionLayerSchema.optional(),
    capabilityBindings: cdlitAiCapabilityBindingsSchema.optional(),
})
    .strict();
export function defaultCdlitAiProductSettings() {
    return {
        aiEnabled: false,
        availabilityEnabled: false,
        bookingEnabled: false,
        bookingRouting: { serviceToCompany: {} },
        siteAssistantVisibility: 'off',
        decisionLayer: defaultCdlitAiDecisionLayer(),
    };
}
export function resolveCdlitSiteAssistantVisibility(settings) {
    if (settings.siteAssistantVisibility) {
        return settings.siteAssistantVisibility;
    }
    return settings.aiEnabled ? 'public' : 'off';
}
/** FAB и публичные session API для анонима / без админ-cookie. */
export function isCdlitSiteAssistantPubliclyVisible(settings) {
    return resolveCdlitSiteAssistantVisibility(settings) === 'public';
}
export function isCdlitSiteAssistantOperationallyEnabled(settings) {
    return resolveCdlitSiteAssistantVisibility(settings) !== 'off';
}
export function cdlitAiPublicGateEnabled(settings, landingAdminSession) {
    const visibility = resolveCdlitSiteAssistantVisibility(settings);
    if (visibility === 'public')
        return true;
    if (visibility === 'admin_only' && landingAdminSession)
        return true;
    return false;
}
export function parseCdlitAiProductSettings(raw) {
    const parsed = cdlitAiProductSettingsSchema.safeParse(raw);
    if (!parsed.success) {
        return defaultCdlitAiProductSettings();
    }
    const visibility = resolveCdlitSiteAssistantVisibility(parsed.data);
    return {
        ...parsed.data,
        siteAssistantVisibility: visibility,
        aiEnabled: visibility !== 'off',
        decisionLayer: parsed.data.decisionLayer ?? defaultCdlitAiDecisionLayer(),
    };
}
export function mergeCdlitAiProductSettings(base, patch) {
    const merged = {
        aiEnabled: patch.aiEnabled ?? base.aiEnabled,
        availabilityEnabled: patch.availabilityEnabled ?? base.availabilityEnabled,
        bookingEnabled: patch.bookingEnabled ?? base.bookingEnabled,
        bookingRouting: patch.bookingRouting ?? base.bookingRouting,
        siteAssistantVisibility: patch.siteAssistantVisibility ?? base.siteAssistantVisibility,
        decisionLayer: patch.decisionLayer ?? base.decisionLayer ?? defaultCdlitAiDecisionLayer(),
        capabilityBindings: patch.capabilityBindings ?? base.capabilityBindings,
    };
    const visibility = resolveCdlitSiteAssistantVisibility(merged);
    return {
        ...merged,
        siteAssistantVisibility: visibility,
        aiEnabled: visibility !== 'off',
    };
}
/** Public site gate — no secrets; drives FAB visibility + launcher identity. */
export const cdlitAiPublicStatusSchema = z
    .object({
    aiEnabled: z.boolean(),
    availabilityEnabled: z.boolean(),
    nativeBookingEnabled: z.boolean(),
    /** Guided panel.title — shown on launcher (e.g. «Афина»). */
    assistantName: z.string().min(1).max(200).optional(),
    /** Guided panel.lead — role/title (e.g. «Администратор»). */
    assistantTitle: z.string().min(1).max(200).optional(),
    /** Opening variants from guidedContent; teaser never owns separate public copy. */
    teaserGreetings: z
        .object({
        morning: z.array(z.string().min(1).max(4000)),
        day: z.array(z.string().min(1).max(4000)),
        evening: z.array(z.string().min(1).max(4000)),
    })
        .strict()
        .optional(),
})
    .strict();
//# sourceMappingURL=product-settings.js.map